import {
    MenuItem, Grid,
    TextField, Typography, CardHeader, Card, CardContent,
    Stack
} from "@mui/material";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import React from "react";
import MergeTagWidget from 'components/broadcast/components/broadcast-page/merge-tags';
import { EmojiPickerButton } from 'common/components/buttons';
import UrlShortenerWidget from 'components/broadcast/components/broadcast-page/url-shortener-widget';
import { AutomationConstant } from '../../api/automation-constants';
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { handleAddUrlShortner, handleEmojiSelect, OpenMergeTagsWindow, MergeTagConstants } from "common/utils/merge-tag-helper";
import { TaskType } from "components/lead/models/task";

const fieldNames = {
    taskSubject: "taskSubject",
    assignedUserId: "assignedUserId",
    taskDueDays: "taskDueDays",
    taskTypeId: "taskTypeId",
    taskNotes: "taskNotes",
    isTask: "isTask"
} as const;

const commonTextFieldProps = {
    fullWidth: true,
    size: "small" as const,
    variant: "outlined" as const,
};

interface TaskSettingsProps {
    initialValues?: any;
    filteredUsers: { crmUserId: string; username: string }[];
    allTaskTypes: TaskType[];
}

const TaskSettings = forwardRef<FormikProps<any>, TaskSettingsProps>((props: TaskSettingsProps, ref) => {
    const { initialValues, filteredUsers, allTaskTypes } = props;
    const taskNotesRef = useRef<HTMLInputElement>(null);
    const taskSubjectRef = useRef<HTMLInputElement>(null);

    const validationSchema = useMemo(() => Yup.object<any>().shape<any>({

        isTask: Yup.boolean().required(),
        taskSubject: Yup.string().nullable().when(fieldNames.isTask, {
            is: true,
            then: (schema: any) => schema.required("Task subject is required")
                .max(AutomationConstant.taskSubjectLimit, AutomationConstant.MaximumLengthAllowed(AutomationConstant.taskSubjectLimit)),
        }),
        taskTypeId: Yup.number().nullable().when(fieldNames.isTask, {
            is: true,
            then: (schema: any) => schema.required("Task type is required").moreThan(0, "Task type is required"),
        }),
        // taskDueDays: Yup.number().nullable().when(fieldNames.isTask, {
        //     is: true,
        //     then: (schema: any) => schema.required("Due Day is required")
        //         .min(1, "Minimum value is 1 day")
        //         .max(14, "Due Day cannot exceed 14 days"),
        // }),
        assignedUserId: Yup.string().nullable().when(fieldNames.isTask, {
            is: true,
            then: (schema: any) => schema.required("Assignment is required"),
        }),
        taskNotes: Yup.string().nullable().when(fieldNames.isTask, {
            is: true,
            then: (schema: any) => schema.max(AutomationConstant.taskNotesLimit, AutomationConstant.MaximumLengthAllowed(AutomationConstant.taskNotesLimit)),
        }),

    }), []);

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
        },
    });

    useImperativeHandle(ref, () => formik);

    const userOptions = useMemo(() => (
        filteredUsers?.map((options) => (
            <MenuItem key={options.crmUserId} value={options.crmUserId}>
                {options.username}
            </MenuItem>
        ))
    ), [filteredUsers]);

    const taskTypeOptions = useMemo(() => (
  [
    <MenuItem key={-1} value={-1}>
      Select Task Type
    </MenuItem>,
    ...allTaskTypes?.map((option) => (
      <MenuItem key={option.id} value={option.id}>
        {option.value} ({option.taskCategory?.value})
      </MenuItem>
    ))
  ]
), [allTaskTypes]);

    const handleNotesMergeTag = useCallback(
        (body: string) => {
            OpenMergeTagsWindow(taskNotesRef, body, fieldNames.taskNotes, formik.setFieldValue, formik.setFieldError, AutomationConstant.taskNotesLimit);
        },
        [formik.setFieldValue, formik.setFieldError]);

    const handleNotesEmoji = useCallback(
        (body: string) => {
            handleEmojiSelect(taskNotesRef, body, fieldNames.taskNotes, formik.setFieldValue, formik.setFieldError, AutomationConstant.taskNotesLimit);
        },
        [formik.setFieldValue, formik.setFieldError]);

    const handleNotesUrlShortner = useCallback(
        (body: string) => {
            handleAddUrlShortner(taskNotesRef, body, fieldNames.taskNotes, formik.setFieldValue, formik.setFieldError, AutomationConstant.taskNotesLimit);
        },
        [formik.setFieldValue, formik.setFieldError]);

    const handleSubjectMergeTag = useCallback(
        (body: string) => {
            OpenMergeTagsWindow(taskSubjectRef, body, fieldNames.taskSubject, formik.setFieldValue, formik.setFieldError,AutomationConstant.taskSubjectLimit);
        },
        [formik.setFieldValue, formik.setFieldError]);

    return (
        <Card variant="outlined" style={{ borderColor: '#ccc', marginTop: "5px" }}>
            <CardHeader
                title={<Typography variant="subtitle1">Task</Typography>}
            />

            <CardContent style={{ paddingTop: "0px", paddingBottom: "0px" }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={5} lg={5} >
                        <TextField
                            {...commonTextFieldProps}
                            id="task-subject"
                            label="Subject"
                            name={fieldNames.taskSubject}
                            value={formik.values.taskSubject}
                            onChange={formik.handleChange}
                            error={!!formik.errors.taskSubject}
                            helperText={formik.errors.taskSubject}
                            inputRef={taskSubjectRef}
                        />
                        <Grid item container
                            spacing={1}
                            style={{ marginLeft: "1px",marginTop: "1px" }}
                        >
                            <MergeTagWidget
                                refScreen={MergeTagConstants.AutomationTaskSubject}
                                disabled={false}
                                onClick={handleSubjectMergeTag}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={3} lg={3}  >
                        <TextField
                            select
                            {...commonTextFieldProps}
                            id="task-assigned-user"
                            placeholder="Assignment"
                            label="Assignment"
                            name={fieldNames.assignedUserId}
                            value={formik.values.assignedUserId}
                            onChange={formik.handleChange}
                            error={!!formik.errors.assignedUserId}
                            helperText={formik.errors.assignedUserId}
                        >
                            {userOptions}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4} lg={4}  >
                        <TextField
                            select
                            {...commonTextFieldProps}
                            id="task-type"
                            placeholder="Task Type"
                            label="Task Type"
                            name={fieldNames.taskTypeId}
                            value={formik.values.taskTypeId}
                            onChange={formik.handleChange}
                            error={!!formik.errors.taskTypeId}
                            helperText={formik.errors.taskTypeId}

                        >
                            {taskTypeOptions}
                        </TextField>
                    </Grid>
                    {/* <Grid item xs={12} sm={3} lg={2}>
                        <TextField
                            {...commonTextFieldProps}
                            id="task-due-days"
                            label="Due Day"
                            type="number"
                            name={fieldNames.taskDueDays}
                            value={formik.values.taskDueDays}
                            onChange={formik.handleChange}
                            error={!!formik.errors.taskDueDays}
                            helperText={formik.errors.taskDueDays}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            InputProps={{
                                inputProps: { min: 1, step: 1, max: 14 },
                            }}
                            sx={{
                                "& input[type=number]": {
                                    MozAppearance: "textfield", // Force arrows in Firefox
                                },
                                "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
                                    WebkitAppearance: "auto", // Ensures visibility in Edge & Chrome
                                    margin: 0,
                                    opacity: 1,
                                },
                            }}
                        />
                    </Grid> */}
                </Grid>

                <Grid container>
                    <Grid item xs={12} mr={1}>
                        <TextField
                            inputRef={taskNotesRef}
                            fullWidth
                            multiline
                            rows={2}
                            id="task-notes"
                            name={fieldNames.taskNotes}
                            value={formik.values.taskNotes}
                            label="Notes"
                            variant="outlined"
                            margin="dense"
                            onChange={formik.handleChange}
                            error={!!formik.errors.taskNotes}
                            helperText={formik.errors.taskNotes}
                        />
                    </Grid>
                </Grid>

                <Grid item container spacing={1} style={{ marginLeft: "1px" }} >

                    <MergeTagWidget
                        refScreen={MergeTagConstants.Automation}
                        disabled={false}
                        onClick={handleNotesMergeTag}
                    />
                    <EmojiPickerButton
                        onSelect={handleNotesEmoji}
                        disabled={false} />
                    <UrlShortenerWidget
                        onClick={handleNotesUrlShortner}
                        disabled={false} />
                </Grid>
            </CardContent>
        </Card>
    );
}
);

export default React.memo(TaskSettings);